'use client';
import React from 'react';
import { useRouter } from 'next/navigation';
import { Star, MapPin, Heart } from 'lucide-react';
import { FEATURED_PROPERTIES } from '../data/properties';

export default function FeaturedProperties() {
  const router = useRouter();

  return (
    <section className="py-10 sm:py-14 max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
      {/* Section header */}
      <div className="flex items-end justify-between mb-5 sm:mb-8">
        <div>
          <p className="text-xs font-display font-600 text-primary-500 uppercase tracking-widest mb-1">
            Handpicked for you
          </p>
          <h2 className="font-display text-2xl sm:text-3xl font-800 text-gray-900" style={{ fontWeight: 800 }}>
            Featured Properties
          </h2>
        </div>
        <button
          onClick={() => router?.push('/hotels')}
          className="text-sm font-display font-600 text-primary-500 hover:text-primary-600 transition-colors duration-150 flex items-center gap-1"
          style={{ fontWeight: 600 }}>
          View all →
        </button>
      </div>
      {/* Mobile: horizontal scroll, Desktop: bento grid */}
      <div className="flex gap-3 overflow-x-auto pb-2 sm:pb-0 -mx-4 px-4 sm:mx-0 sm:px-0 sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:gap-4 lg:gap-5 scrollbar-hide">
        {FEATURED_PROPERTIES?.map((prop, idx) => (
          <div
            key={prop?.id}
            onClick={() => router?.push('/hotels')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e?.key === 'Enter' && router?.push('/hotels')}
            aria-label={`View ${prop?.name} in ${prop?.location}`}
            className={`group relative bg-white rounded-2xl overflow-hidden card-shadow hover:card-shadow-lg transition-all duration-300 cursor-pointer flex-shrink-0 w-56 sm:w-auto active:scale-[0.98]
              ${idx === 0 ? 'lg:row-span-2 lg:col-span-2' : ''}`}>

            {/* Image */}
            <div className={`relative overflow-hidden ${idx === 0 ? 'h-44 sm:h-56 lg:h-full lg:min-h-[340px]' : 'h-36 sm:h-48'}`}>
              <img
                src={prop?.image}
                alt={prop?.alt}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                loading={idx === 0 ? 'eager' : 'lazy'}
              />
              {prop?.tag && (
                <span
                  className={`absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-display font-600 text-white ${prop?.tagColor}`}
                  style={{ fontWeight: 600 }}>
                  {prop?.tag}
                </span>
              )}
              <button
                onClick={(e) => e?.stopPropagation()}
                className="absolute top-3 right-3 p-2 rounded-full bg-white/80 backdrop-blur-sm text-gray-500 hover:text-primary-500 transition-colors duration-150 min-w-[36px] min-h-[36px] flex items-center justify-center"
                aria-label={`Save ${prop?.name} to wishlist`}>
                <Heart size={15} />
              </button>
              <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/50 to-transparent" />
            </div>

            {/* Info */}
            <div className="p-3 sm:p-4">
              <h3 className="font-display font-700 text-gray-900 text-xs sm:text-base leading-snug mb-1" style={{ fontWeight: 700 }}>
                {prop?.name}
              </h3>
              <div className="flex items-center gap-1 text-gray-500 text-xs font-body mb-2 sm:mb-3">
                <MapPin size={12} className="text-primary-400 flex-shrink-0" />
                <span className="truncate">{prop?.location}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <Star size={13} className="text-yellow-400 fill-yellow-400" />
                  <span className="text-sm font-display font-600 text-gray-800" style={{ fontWeight: 600 }}>
                    {prop?.rating}
                  </span>
                  <span className="text-xs text-gray-400 font-body hidden sm:inline">({prop?.reviews})</span>
                </div>
                <div className="text-right">
                  <span className="font-display font-700 text-primary-500 text-xs sm:text-base" style={{ fontWeight: 700 }}>
                    {prop?.price?.toLocaleString()}
                  </span>
                  <span className="text-xs text-gray-400 font-body ml-1 hidden sm:inline">{prop?.currency}/night</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}